import React, { useState, useRef } from 'react';
import { 
    Button, 
    TextField, 
    Box, 
    Typography, 
    InputAdornment, 
    Paper, 
    CircularProgress,
    IconButton
} from '@mui/material';
import { 
    UploadCloud, 
    Link as LinkIcon, 
    FileCheck, 
    X, 
    FileText 
} from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PALETTE = {
  ice: "#C2EDF0",
  sky: "#99D9DF",
  aqua: "#00C0CA",
  teal: "#008B9C",
  dark: "#22404D",
  azur: "#28708B"
};

function SubirArchivoComunicacion({ pedidoId, tareaId, actualizar }) {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const [link, setLink] = useState('');
    const [archivo, setArchivo] = useState(null);
    const fileInputRef = useRef(null);
    const [cargando, setCargando] = useState(false);

    const cambioElArchivo = (event) => {
        if (event.target.files && event.target.files[0]) {
            setArchivo(event.target.files[0]);
        }
    };

    const quitarArchivo = () => {
        setArchivo(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const cambioElLink = (event) => {
        setLink(event.target.value);
    };

    function esLink(cadena) {
        try {
            new URL(cadena);
            return true;
        } catch (_) {
            return false;
        }
    }
    
    const subirArchivo = async () => {
        if (!archivo && !link) {
            toast.info('Por favor, selecciona un archivo o proporciona un enlace.');
            return;
        }

        const formData = new FormData();
        if (archivo) {
            formData.append('file', archivo);
            formData.append('nombreArchivo', archivo.name);
        } else {
            formData.append('file', '');
           
            if(!esLink(link)){
                toast.error("El enlace proporcionado no es válido.");
                return;
            } else {
                const fileNameFromLink = new URL(link).pathname.split('/').pop() || 'enlace_comunicacion';
                formData.append('nombreArchivo', fileNameFromLink);
            }
        }

        formData.append('id', 0);
        formData.append('idPedido', pedidoId);
        formData.append('idTarea', tareaId);
        formData.append('urlAzure', '');
        formData.append('tipo', 'Comunicacion');
        formData.append('link', link || '');
        
        setCargando(true);
        try {
            const response = await fetch(`${apiUrl}/Archivo/subirComunicacion`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                if (response.status === 500) {
                    toast.error('Ya existe un archivo con este nombre para el pedido');
                    return;
                } else {
                    throw new Error(`Error HTTP: ${response.status}`);
                }
            }

            setArchivo(null);
            setLink('');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            toast.success('Material subido correctamente');
            actualizar();
        } catch (error) {
            toast.error(`Error al subir: ${error.message}`);
        } finally {
            setCargando(false);  
        }
    };

    return (
        <Paper elevation={0} sx={{ 
            p: 3, 
            borderRadius: '24px', 
            backgroundColor: 'white', 
            border: `1px solid ${PALETTE.sky}`,
            mb: 3
        }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 800, color: PALETTE.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
                <UploadCloud size={20} color={PALETTE.aqua} /> Adjuntar Entregables
            </Typography>

            {/* Zona de Selección de Archivo */}
            <Box sx={{ mb: 2 }}>
                {!archivo ? (
                    <Button
                        component="label"
                        fullWidth
                        sx={{
                            height: '100px',
                            borderRadius: '16px',
                            border: `2px dashed ${PALETTE.sky}`,
                            display: 'flex',
                            flexDirection: 'column',
                            color: PALETTE.azur,
                            textTransform: 'none',
                            '&:hover': { backgroundColor: `${PALETTE.ice}20`, borderColor: PALETTE.aqua }
                        }}
                    >
                        <UploadCloud size={28} style={{ marginBottom: '8px' }} />
                        <Typography variant="body2" fontWeight={600}>Haga clic para subir archivo</Typography>
                        <input type="file" hidden ref={fileInputRef} onChange={cambioElArchivo} />
                    </Button>
                ) : (
                    <Box sx={{ 
                        p: 2, borderRadius: '16px', bgcolor: PALETTE.pearl, 
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        border: `1px solid ${PALETTE.aqua}`
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <FileText color={PALETTE.teal} size={24} />
                            <Box>
                                <Typography variant="body2" fontWeight={700} sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {archivo.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {(archivo.size / 1024).toFixed(1)} KB
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={quitarArchivo} size="small">
                            <X size={18} />
                        </IconButton>
                    </Box>
                )}
            </Box>

            <Typography variant="caption" sx={{ color: PALETTE.azur, fontWeight: 700, ml: 1, mb: 0.5, display: 'block' }}>
                O PEGAR UN ENLACE
            </Typography>

            <TextField
                placeholder="Ej: Canva, Drive, YouTube..."
                value={link}
                onChange={cambioElLink}
                fullWidth
                variant="outlined"
                disabled={!!archivo} // Deshabilitar si ya hay un archivo para evitar confusión
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <LinkIcon size={18} color={PALETTE.teal} />
                        </InputAdornment>
                    ),
                    sx: { borderRadius: '14px', backgroundColor: '#fafafa' }
                }}
                sx={{ mb: 3 }}
            />

            <Button
                onClick={subirArchivo}
                disabled={cargando || (!archivo && !link)}
                variant="contained"
                fullWidth
                startIcon={cargando ? <CircularProgress size={20} color="inherit" /> : <FileCheck size={20} />}
                sx={{
                    py: 1.5,
                    borderRadius: '14px',
                    backgroundColor: PALETTE.dark,
                    fontWeight: 800,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: PALETTE.teal, transform: 'translateY(-2px)' },
                    transition: 'all 0.3s ease'
                }}
            >
                {cargando ? 'Subiendo material...' : 'Confirmar Subida'}
            </Button>
        </Paper>
    );
}

export default SubirArchivoComunicacion;